import { useState } from 'react';
import { X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@sara/ui/components/popover';
import { cn } from '@sara/ui/lib/utils';
import type { FilterDef, FilterEditorProps } from './types';

interface FilterChipProps {
  def: FilterDef;
  editorProps: FilterEditorProps;
  onRemove: () => void;
  defaultOpen?: boolean;
}

export function FilterChip({ def, editorProps, onRemove, defaultOpen = false }: FilterChipProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { Icon, label, chipValue } = def;
  const value = chipValue(editorProps.search, editorProps.interviewTypes);

  return (
    <div className="inline-flex h-7 items-center rounded-full border border-primary/30 bg-primary/8 text-[12.5px] font-medium text-primary">
      <Popover
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) editorProps.onClose();
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex h-full items-center gap-1.5 rounded-l-full pl-2 pr-1 transition-colors hover:bg-primary/12'
            )}
            aria-pressed={open}
          >
            <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.6} />
            <span>{label}:</span>
            <span className="max-w-[140px] truncate">{value}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-2" onClick={(e) => e.stopPropagation()}>
          <def.EditorComponent {...editorProps} onClose={() => setOpen(false)} />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        className="ml-0.5 mr-1 flex h-5 w-5 items-center justify-center rounded-full hover:bg-primary/20"
        onClick={onRemove}
      >
        <X className="h-3 w-3" strokeWidth={1.6} />
      </button>
    </div>
  );
}
